/**
 * DocumentLinkedChecklist Component
 *
 * Read-only display of checklist items linked to a document,
 * grouped by category with expandable cards showing clause refs
 * and example documents.
 */

import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "@audit-app/utils";
import {
  ChevronDown,
  BookOpen,
  FileText,
  CheckCircle2,
  ExternalLink,
  Unlink,
  ListChecks,
  Link2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import type { Id, DocumentId, ChecklistItemId } from "@/packages/types";
import {
  useLinkedChecklistItems,
  type LinkedChecklistItem,
} from "@/hooks/use-linked-checklist-items";
import { useUnlinkDocument } from "@/hooks/use-unlink-document";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { LinkChecklistItemDialog } from "./LinkChecklistItemDialog";

interface DocumentLinkedChecklistProps {
  documentId: DocumentId;
  auditId?: Id<"audits">;
  onNavigateToItem?: (
    checklistItemId: ChecklistItemId,
    auditId: string | null,
  ) => void;
}

interface CategoryGroup {
  categoryId: string;
  categoryName: string;
  items: LinkedChecklistItem[];
}

function ClauseRefPill({ clause }: { clause: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-400 border border-sky-500/15">
      <BookOpen size={10} />
      {clause}
    </span>
  );
}

function LinkedItemCard({
  item,
  onNavigate,
  onUnlink,
}: {
  item: LinkedChecklistItem;
  onNavigate?: (
    checklistItemId: ChecklistItemId,
    auditId: string | null,
  ) => void;
  onUnlink?: (checklistItemId: ChecklistItemId) => void;
}) {
  const { t } = useTranslation("documents");
  const [isExpanded, setIsExpanded] = useState(false);

  const hasExpandableContent =
    item.clauseRefs.length > 0 || item.exampleDocuments.length > 0;

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      {/* Collapsed row */}
      <button
        type="button"
        onClick={() => {
          if (hasExpandableContent) {
            setIsExpanded(!isExpanded);
          }
        }}
        onKeyDown={(e) => {
          if (hasExpandableContent && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
        className={`flex items-center gap-2.5 p-3 w-full appearance-none border-0 bg-transparent text-left ${hasExpandableContent ? "cursor-pointer" : ""}`}
      >
        <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />

        <p className="text-[13px] text-primary font-medium truncate flex-1 text-left">
          {item.title}
        </p>

        {onUnlink && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onUnlink(item.checklistItemId);
            }}
            className="shrink-0 p-1 rounded hover:bg-red-500/10 text-muted hover:text-red-400 transition-colors"
            title={t("actions.unlinkFromChecklist")}
          >
            <Unlink size={14} />
          </button>
        )}

        {onNavigate && item.auditId && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(item.checklistItemId, item.auditId);
            }}
            className="shrink-0 p-1 rounded hover:bg-card-hover text-muted hover:text-accent transition-colors"
            title={t("actions.viewInChecklist")}
          >
            <ExternalLink size={14} />
          </button>
        )}

        {hasExpandableContent && (
          <ChevronDown
            size={14}
            className={`shrink-0 text-muted transition-transform ${isExpanded ? "rotate-180" : ""}`}
          />
        )}
      </button>

      {/* Expanded details */}
      <AnimatePresence>
        {isExpanded && hasExpandableContent && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 space-y-2.5 border-t border-border/50 pt-2.5 ml-[34px]">
              {/* Clause refs */}
              {item.clauseRefs.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {item.clauseRefs.map((clause) => (
                    <ClauseRefPill key={clause} clause={clause} />
                  ))}
                </div>
              )}

              {/* Example documents */}
              {item.exampleDocuments.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[10px] uppercase text-muted font-medium tracking-wider">
                    Example documents
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {item.exampleDocuments.map((doc) => (
                      <span
                        key={doc.name}
                        className="inline-flex items-center gap-1 text-[11px] text-muted px-1.5 py-0.5 rounded bg-card-hover"
                      >
                        <FileText size={10} />
                        {doc.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function DocumentLinkedChecklist({
  documentId,
  auditId,
  onNavigateToItem,
}: DocumentLinkedChecklistProps) {
  const { t } = useTranslation("documents");
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [pendingUnlinkId, setPendingUnlinkId] =
    useState<ChecklistItemId | null>(null);
  const { items, isLoading } = useLinkedChecklistItems(documentId);
  const unlinkDocument = useUnlinkDocument();

  const handleUnlinkRequest = useCallback(
    (checklistItemId: ChecklistItemId) => {
      setPendingUnlinkId(checklistItemId);
    },
    [],
  );

  const handleUnlinkConfirm = useCallback(async () => {
    if (pendingUnlinkId === null) return;
    await unlinkDocument({ documentId, checklistItemId: pendingUnlinkId });
    setPendingUnlinkId(null);
  }, [pendingUnlinkId, unlinkDocument, documentId]);

  const handleUnlinkCancel = useCallback(() => {
    setPendingUnlinkId(null);
  }, []);

  // Group items by category
  const categoryGroups: CategoryGroup[] = useMemo(() => {
    const groupMap = new Map<string, CategoryGroup>();
    for (const item of items) {
      const key = item.categoryId ?? "uncategorized";
      const existing = groupMap.get(key);
      if (existing) {
        existing.items.push(item);
      } else {
        groupMap.set(key, {
          categoryId: key,
          categoryName: item.categoryName,
          items: [item],
        });
      }
    }
    return [...groupMap.values()];
  }, [items]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <span className="text-[13px] text-muted">Loading...</span>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <>
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          <div className="w-10 h-10 rounded-full bg-card-hover flex items-center justify-center mb-3">
            <ListChecks size={20} className="text-muted" />
          </div>
          <p className="text-[13px] font-medium text-primary mb-1">
            No checklist items linked
          </p>
          <p className="text-[12px] text-muted max-w-[220px] mb-4">
            Link this document to a checklist item to track it as evidence.
          </p>
          <button
            type="button"
            onClick={() => setLinkDialogOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium bg-accent/10 text-accent hover:bg-accent/20 transition-colors"
          >
            <Link2 size={14} />
            Link to checklist item
          </button>
        </div>
        <LinkChecklistItemDialog
          documentId={documentId}
          auditId={auditId}
          open={linkDialogOpen}
          onOpenChange={setLinkDialogOpen}
        />
      </>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="space-y-3"
      >
        <div className="flex items-center justify-between">
          <p className="text-[11px] uppercase text-muted font-medium tracking-wider">
            Linked checklist items ({items.length})
          </p>
          <button
            type="button"
            onClick={() => setLinkDialogOpen(true)}
            className="inline-flex items-center gap-1 text-[12px] font-medium text-accent hover:text-accent/80 transition-colors"
          >
            <Link2 size={12} />
            Link another
          </button>
        </div>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {categoryGroups.map((group) => (
            <div key={group.categoryId}>
              <p className="text-[11px] font-medium text-secondary mb-1.5 px-0.5">
                {group.categoryName}
              </p>
              <div className="space-y-2">
                {group.items.map((item) => (
                  <LinkedItemCard
                    key={item.checklistItemId}
                    item={item}
                    onNavigate={onNavigateToItem}
                    onUnlink={handleUnlinkRequest}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
      <LinkChecklistItemDialog
        documentId={documentId}
        auditId={auditId}
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
      />
      <ConfirmDialog
        open={pendingUnlinkId !== null}
        onClose={handleUnlinkCancel}
      >
        <ConfirmDialog.Title>{t("unlinkConfirm.title")}</ConfirmDialog.Title>
        <ConfirmDialog.Body>
          <p>{t("unlinkConfirm.description")}</p>
        </ConfirmDialog.Body>
        <ConfirmDialog.Actions
          onConfirm={handleUnlinkConfirm}
          confirmLabel={t("unlinkConfirm.confirm")}
          variant="danger"
        />
      </ConfirmDialog>
    </>
  );
}
